import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { itemService } from '@/services/itemService'

import type { SupplyChainGrn, SupplyChainGrnLine, SupplyChainGrnRow } from '@/modules/supply-chain/goods-receipt-notes/types'
import { computeGrnLineTotals } from '@/modules/supply-chain/goods-receipt-notes/types'

const unwrap = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>
    if ('success' in p && 'data' in p) return p.data
    if ('data' in p) return p.data
  }
  return payload
}

const toArray = (payload: unknown): unknown[] => {
  const raw = unwrap(payload)
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>
    if (Array.isArray(o.data)) return o.data
    if (Array.isArray(o.items)) return o.items
    if (Array.isArray(o.result)) return o.result
  }
  return []
}

function num(v: unknown, fallback = 0) {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}
function str(v: unknown) {
  return typeof v === 'string' ? v : String(v ?? '')
}
function dateOnly(v: unknown): string {
  const s = str(v).trim()
  return s.includes('T') ? s.split('T')[0] : s
}

function toBillFlag(raw: Record<string, unknown>): boolean {
  const v = raw.isBillCreated ?? raw.IsBillCreated ?? raw.isbillcreated ?? raw.is_bill_created
  return v === true || v === 1 || (typeof v === 'string' && v.toLowerCase() === 'true')
}

function mapLine(raw: Record<string, unknown>): SupplyChainGrnLine {
  return {
    id: str(raw.id ?? raw.Id ?? raw.lineId ?? raw.LineId ?? ''),
    itemId: str(raw.itemId ?? raw.ItemId ?? ''),
    itemName: str(raw.itemName ?? raw.ItemName ?? raw.itemNameEn ?? raw.ItemNameEn ?? raw.itemNameAr ?? raw.ItemNameAr ?? ''),
    orderQuantity: num(raw.quantity ?? raw.Quantity ?? raw.orderQuantity ?? raw.OrderQuantity ?? 0),
    quantityReceived: num(raw.quantityReceived ?? raw.QuantityReceived ?? 0),
    toReceive: num(raw.toReceive ?? raw.ToReceive ?? 0),
    unitPrice: num(raw.unitPrice ?? raw.UnitPrice ?? 0),
    discountPercent: num(raw.discount ?? raw.Discount ?? raw.discountPercent ?? raw.DiscountPercent ?? 0),
    costLedgerAccountId: str(
      raw.costLedgerAccountId ??
        raw.CostLedgerAccountId ??
        raw.costLedgerAccount ??
        raw.CostLedgerAccount ??
        '',
    ),
    taxPercent: num(raw.taxPercent ?? raw.TaxPercent ?? 0),
  }
}

function mapRow(raw: Record<string, unknown>): SupplyChainGrnRow {
  const total = num(raw.totalAmount ?? raw.total ?? raw.TotalAmount ?? raw.Total ?? 0)
  const status = str(raw.status ?? raw.Status ?? '').trim() as any
  return {
    id: str(raw.id ?? raw.Id ?? ''),
    code: str(raw.code ?? raw.Code ?? raw.grnnumber ?? raw.Grnnumber ?? '').trim(),
    reference: str(raw.reference ?? raw.Reference ?? '').trim(),
    warehouse: str(raw.wharehouseName ?? raw.WharehouseName ?? raw.warehouseName ?? raw.WarehouseName ?? raw.warehouse ?? '').trim(),
    vendor: str(raw.vendorNameEn ?? raw.VendorNameEn ?? raw.vendorName ?? raw.VendorName ?? '').trim(),
    orderCode: str(raw.purchaseorderCode ?? raw.PurchaseorderCode ?? raw.purchaseOrderCode ?? raw.PurchaseOrderCode ?? raw.orderCode ?? '').trim(),
    date: dateOnly(raw.date ?? raw.Date ?? raw.orderDate ?? raw.OrderDate ?? ''),
    total,
    status: status || 'open',
    isBillCreated: toBillFlag(raw),
  }
}

function mapMaster(raw: Record<string, unknown>, lines: SupplyChainGrnLine[]): SupplyChainGrn {
  const status = str(raw.status ?? raw.Status ?? '').trim() as any
  return {
    id: str(raw.id ?? raw.Id ?? ''),
    code: str(raw.code ?? raw.Code ?? raw.grnnumber ?? raw.Grnnumber ?? '').trim(),
    reference: str(raw.reference ?? raw.Reference ?? '').trim(),
    vendor: str(raw.vendorNameEn ?? raw.VendorNameEn ?? raw.vendorName ?? raw.VendorName ?? '').trim(),
    warehouse: str(raw.wharehouseName ?? raw.WharehouseName ?? raw.warehouseName ?? raw.WarehouseName ?? raw.warehouse ?? '').trim(),
    purchaseOrderCode: str(raw.purchaseorderCode ?? raw.PurchaseorderCode ?? raw.purchaseOrderCode ?? raw.PurchaseOrderCode ?? raw.orderCode ?? '').trim(),
    orderDate: dateOnly(raw.orderDate ?? raw.OrderDate ?? raw.date ?? raw.Date ?? ''),
    grnDate: dateOnly(raw.date ?? raw.Date ?? ''),
    grnReference: str(raw.taxNotes ?? raw.TaxNotes ?? raw.reference ?? raw.Reference ?? '').trim(),
    attachmentName: '',
    status: status || 'open',
    isBillCreated: toBillFlag(raw),
    lines,
    createdAt: '',
    updatedAt: '',
  }
}

export class ScGoodsReceiptNoteService {
  private appendFormField(formData: FormData, key: string, value: unknown) {
    if (value === undefined || value === null) {
      formData.append(key, '')
      return
    }
    if (typeof value === 'boolean') {
      formData.append(key, value ? 'true' : 'false')
      return
    }
    formData.append(key, String(value))
  }

  /** Swagger Lines schema (ScGrn/Save) */
  private normalizeLineForJson(l: SupplyChainGrnLine, grnId: number): Record<string, number | string> {
    const t = computeGrnLineTotals(l)
    const itemId = num(l.itemId, 0)
    const taxId = num(l.taxId, 0)
    const orderLineId = num(l.id, 0)
    return {
      discount: 0,
      taxAmount: Math.round(t.taxAmount * 10000) / 10000,
      unitPrice: num(l.unitPrice, 0),
      quantity: num(l.orderQuantity, 0),
      tax: taxId,
      lineAmount: Math.round(t.lineTotal * 10000) / 10000,
      itemId,
      costLedgerAccountId: num(l.costLedgerAccountId, 0),
      isDeleted: 'false',
      discountAmount: Math.round(t.discountAmount * 10000) / 10000,
      id: 0,
      description: '',
      taxableAmount: Math.round(t.taxableAmount * 10000) / 10000,
      orderLineId,
      grnId,
    }
  }

  private buildLinesJson(lines: SupplyChainGrnLine[], grnId: number): string {
    return JSON.stringify((lines || []).map((l) => this.normalizeLineForJson(l, grnId)))
  }

  private buildSaveFormData(payload: {
    id: number
    code: string
    orderId: number
    orderCode: string
    vendorId: number
    vendorName: string
    date: string
    branchId: number
    reference: string
    attachmentPath?: string
    taxableAmount: number
    taxAmount: number
    discount: number
    total: number
    isBillCreated: boolean
    status: number
    createdDate: string
    createdBy: number
    updatedDate: string
    updatedById: number
    attachmentFile?: File | null
    lines: SupplyChainGrnLine[]
  }): FormData {
    const fd = new FormData()

    this.appendFormField(fd, 'Id', payload.id)
    this.appendFormField(fd, 'Code', payload.code)
    this.appendFormField(fd, 'OrderId', payload.orderId)
    this.appendFormField(fd, 'OrderCode', payload.orderCode)
    this.appendFormField(fd, 'VendorId', payload.vendorId)
    this.appendFormField(fd, 'VendorName', payload.vendorName)
    this.appendFormField(fd, 'Date', payload.date)
    this.appendFormField(fd, 'BranchId', payload.branchId)
    this.appendFormField(fd, 'Reference', payload.reference)
    this.appendFormField(fd, 'AttachmentPath', payload.attachmentPath ?? '')
    this.appendFormField(fd, 'TaxableAmount', payload.taxableAmount)
    this.appendFormField(fd, 'TaxAmount', payload.taxAmount)
    this.appendFormField(fd, 'Discount', payload.discount)
    this.appendFormField(fd, 'Total', payload.total)
    this.appendFormField(fd, 'IsBillCreated', payload.isBillCreated)
    this.appendFormField(fd, 'Status', payload.status)
    this.appendFormField(fd, 'CreatedDate', payload.createdDate)
    this.appendFormField(fd, 'CreatedBy', payload.createdBy)
    this.appendFormField(fd, 'UpdatedDate', payload.updatedDate)
    this.appendFormField(fd, 'UpdatedById', payload.updatedById)

    const grnIdForLines = payload.id > 0 ? payload.id : 0
    const linesJson = this.buildLinesJson(payload.lines, grnIdForLines)

    // Send both (Swagger shows both fields).
    this.appendFormField(fd, 'Lines', linesJson)
    this.appendFormField(fd, 'grnLinesJson', linesJson)

    // Also send indexed fields (works with many ASP.NET [FromForm] binders).
    payload.lines.forEach((l, i) => {
      const n = this.normalizeLineForJson(l, grnIdForLines)
      for (const [k, v] of Object.entries(n)) {
        this.appendFormField(fd, `Lines[${i}].${k}`, v)
      }
    })

    if (payload.attachmentFile) {
      fd.append('AttachmentFile', payload.attachmentFile)
    } else {
      this.appendFormField(fd, 'AttachmentFile', '')
    }

    return fd
  }

  async save(payload: Parameters<ScGoodsReceiptNoteService['buildSaveFormData']>[0]): Promise<void> {
    const fd = this.buildSaveFormData(payload)
    await apiClient.post(endpoints.scGrn.save, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // Backend may respond with non-2xx while still persisting; caller shows success + refresh.
      skipHandledLocallyErrorUI: true,
      skipGlobal500Swal: true,
    } as any)
  }

  async update(payload: Parameters<ScGoodsReceiptNoteService['buildSaveFormData']>[0]): Promise<void> {
    const fd = this.buildSaveFormData(payload)
    await apiClient.put(endpoints.scGrn.update, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      skipHandledLocallyErrorUI: true,
      skipGlobal500Swal: true,
    } as any)
  }

  /** Swagger: POST body `{ branchId, grnId, note }` */
  async convertToBillIndividually(branchId: number, grnId: number, note = ''): Promise<void> {
    await apiClient.post(
      endpoints.scGrn.convertToBillIndividually,
      { branchId, grnId, note: note ?? '' },
      // Swagger lists json-patch+json; body is a plain JSON object.
      {
        headers: { 'Content-Type': 'application/json-patch+json' },
        // Backend sometimes returns 500 even when it persisted the conversion.
        // In these cases we still want the caller to show success + refresh.
        validateStatus: (status: number) => (status >= 200 && status < 300) || status === 500,
        skipHandledLocallyErrorUI: true,
        skipGlobal500Swal: true,
      } as any,
    )
  }

  /** Swagger: POST body `{ branchId, grnIds, note }` */
  async convertToBillCombined(branchId: number, grnIds: number[], note = ''): Promise<void> {
    const ids = (grnIds || []).map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0)
    await apiClient.post(
      endpoints.scGrn.convertToBillCombined,
      { branchId, grnIds: ids, note: note ?? '' },
      {
        headers: { 'Content-Type': 'application/json-patch+json' },
        validateStatus: (status: number) => (status >= 200 && status < 300) || status === 500,
        skipHandledLocallyErrorUI: true,
        skipGlobal500Swal: true,
      } as any,
    )
  }

  async getTaxes(branchId: number): Promise<Array<{ id: number; name: string; percent: number }>> {
    const res = await apiClient.get(endpoints.scPurchaseOrder.getTaxes(branchId), {
      skipHandledLocallyErrorUI: true,
    } as any)
    const arr = toArray(res.data)
    return arr
      .map((x) => {
        const r = x && typeof x === 'object' ? (x as Record<string, unknown>) : {}
        const id = num(r.taxRateId ?? r.TaxRateId ?? r.id ?? r.Id ?? 0)
        const name = str(
          r.taxRateNameEn ??
            r.TaxRateNameEn ??
            r.taxRateNameAr ??
            r.TaxRateNameAr ??
            r.lookupNameEn ??
            r.lookupNameAr ??
            r.name ??
            'Tax',
        ).trim()
        const percent = num(r.taxPercent ?? r.TaxPercent ?? r.taxRate ?? r.TaxRate ?? r.percent ?? r.Percent ?? 0)
        return { id, name, percent }
      })
      .filter((t) => t.id > 0)
  }

  async getVendors(branchId: number): Promise<Array<{ id: number; name: string }>> {
    const res = await apiClient.get(endpoints.scGrn.getVendors(branchId), {
      skipHandledLocallyErrorUI: true,
    } as any)
    const arr = toArray(res.data)
    return arr
      .map((x) => {
        const r = x && typeof x === 'object' ? (x as Record<string, unknown>) : {}
        const id = num(r.vendorId ?? r.VendorId ?? r.id ?? r.Id ?? 0)
        const name = str(
          r.vendorNameEn ??
            r.VendorNameEn ??
            r.vendorNameAr ??
            r.VendorNameAr ??
            r.vendorName ??
            r.VendorName ??
            r.name ??
            r.Name,
        ).trim()
        return { id, name }
      })
      .filter((v) => v.id > 0)
  }

  async getPurchaseOrdersByVendorId(
    vendorId: number,
    branchId: number,
  ): Promise<Array<{ id: number; code: string; date: string; reference: string }>> {
    const res = await apiClient.get(endpoints.scGrn.getPurchaseOrdersByVendorId(vendorId, branchId), {
      skipHandledLocallyErrorUI: true,
    } as any)
    const arr = toArray(res.data)
    return arr
      .map((x) => {
        const r = x && typeof x === 'object' ? (x as Record<string, unknown>) : {}
        return {
          id: num(r.id ?? r.Id ?? r.purchaseOrderId ?? r.PurchaseOrderId ?? 0),
          code: str(r.code ?? r.Code ?? r.purchaseorderCode ?? r.PurchaseorderCode ?? '').trim(),
          date: dateOnly(r.date ?? r.Date ?? r.orderDate ?? r.OrderDate ?? ''),
          reference: str(r.reference ?? r.Reference ?? '').trim(),
        }
      })
      .filter((p) => p.id > 0)
  }

  async getPurchaseOrderLines(
    purchaseOrderId: number,
    branchId: number,
  ): Promise<SupplyChainGrnLine[]> {
    const res = await apiClient.get(endpoints.scPurchaseOrder.getLinesByOrderId(purchaseOrderId, branchId), {
      skipHandledLocallyErrorUI: true,
    } as any)
    const arr = toArray(res.data)
    const rawLines = arr
      .map((x) => (x && typeof x === 'object' ? (x as Record<string, unknown>) : {}))
      .map((r) => {
        const itemIdNum = num(r.itemId ?? r.ItemId ?? 0)
        const itemName = str(r.itemName ?? r.ItemName ?? '').trim()
        return {
          id: str(r.id ?? r.Id ?? `po-line-${itemIdNum}`),
          itemId: itemIdNum > 0 ? String(itemIdNum) : '',
          itemName,
          orderQuantity: num(r.quantity ?? r.Quantity ?? 0),
          quantityReceived: 0,
          toReceive: 0,
          unitPrice: num(r.unitPrice ?? r.UnitPrice ?? 0),
          discountPercent: num(r.discountPercent ?? r.DiscountPercent ?? r.discount ?? r.Discount ?? 0),
          taxId: str(r.tax ?? r.Tax ?? r.taxId ?? r.TaxId ?? ''),
          taxPercent: num(r.taxPercent ?? r.TaxPercent ?? 0),
        } satisfies SupplyChainGrnLine
      })
      .filter((l) => Number(l.orderQuantity || 0) > 0 && String(l.itemId || '').trim())

    // Fill missing item names via Item API (cached in itemService internals per request; OK for now)
    const missing = rawLines.filter((l) => !String(l.itemName || '').trim() && Number(l.itemId) > 0)
    if (missing.length === 0) return rawLines
    const pairs = await Promise.all(
      missing.map(async (l) => {
        try {
          const it = await itemService.getItemById(Number(l.itemId), branchId)
          const name = (it.itemNameEn || it.itemNameAr || it.itemCode || '').trim()
          return { itemId: String(l.itemId), name }
        } catch {
          return { itemId: String(l.itemId), name: '' }
        }
      }),
    )
    const nameById = new Map(pairs.map((p) => [p.itemId, p.name]))
    return rawLines.map((l) => ({
      ...l,
      itemName: String(l.itemName || '').trim() ? l.itemName : nameById.get(String(l.itemId)) || '',
    }))
  }

  async getAll(
    branchId: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<{ items: SupplyChainGrnRow[]; totalCount: number }> {
    const res = await apiClient.get(endpoints.scGrn.getAll(branchId, pageNumber, pageSize), {
      skipHandledLocallyErrorUI: true,
    } as any)
    const payload = res.data
    const top = payload && typeof payload === 'object' && !Array.isArray(payload) ? (payload as Record<string, unknown>) : null
    const raw = unwrap(payload)
    let list = toArray(raw)
    if (list.length === 0 && top && Array.isArray(top.items)) list = top.items as unknown[]
    const items = list
      .map((x) => (x && typeof x === 'object' ? mapRow(x as Record<string, unknown>) : null))
      .filter(Boolean) as SupplyChainGrnRow[]
    const totalCount = num(
      (raw && typeof raw === 'object' && !Array.isArray(raw)
        ? (raw as Record<string, unknown>).totalRecords ?? (raw as Record<string, unknown>).totalCount
        : undefined) ??
        top?.totalRecords ??
        top?.totalCount ??
        items.length,
      items.length,
    )
    return { items, totalCount }
  }

  async getById(id: number, branchId: number): Promise<SupplyChainGrn | null> {
    const [mRes, lRes] = await Promise.all([
      apiClient.get(endpoints.scGrn.getById(id, branchId)),
      apiClient.get(endpoints.scGrn.getGrnLinesByGrnId(id, branchId)),
    ])
    const mRaw = unwrap(mRes.data)
    const master = Array.isArray(mRaw) ? mRaw[0] : mRaw
    const masterObj = master && typeof master === 'object' ? (master as Record<string, unknown>) : null
    if (!masterObj) return null
    const linesArr = toArray(unwrap(lRes.data))
    const lines = linesArr
      .map((x) => (x && typeof x === 'object' ? mapLine(x as Record<string, unknown>) : null))
      .filter(Boolean) as SupplyChainGrnLine[]
    return mapMaster(masterObj, lines)
  }
}

export const scGoodsReceiptNoteService = new ScGoodsReceiptNoteService()

